'use strict';

import { flow, observable, configure, action, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import feedbackMessagesStore from '@uw-it-sis/lib-react/lib/FeedbackMessagesStore';
import events from '@uw-it-sis/lib-react/lib/Events';
import NotifyEvents from './NotifyEvents';
import NotifyUtils from './NotifyUtils';
import NotifyStore from './NotifyStore';
import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils'

import {capitalize} from '@uw-it-sis/lib-react/lib/Utils';
import {MetricLabels} from './Constants';

class NotifySubscriptionFormStore {

    state = States.initial;

    formData = this.getDefaultFormData();

    additionalAnalytics = {};

    constructor() {
        makeObservable(this, {
            state: observable,
            formData: observable,
            setFormData: action,
            clearFormData: action
        });

        this.actionPendingItem;
        this.analyticsStore = appStore.getAnalyticsStore();
    }

    /**
     * This is the starting formData object
     */
    getDefaultFormData() {
        return ({
            activityId: undefined,
            protocols: {
                email: false,
                sms: false
            }
        });
    }

    /**
     * Setup the formdata with the activity id of section and set whether protocols are subscribed or not
     * @param activityId of the section
     */
    mount(activityId) {
        this.setFormData('activityId', activityId);

        if (NotifyStore.isSubscribed(activityId)) {
            for (let protocol of NotifyUtils.protocols) {
                if (NotifyStore.isEndPointSubscribed(activityId, protocol)) {
                    this.setFormData('protocols:' + protocol, true);
                }
            }
        }
    }

    /**
     * Set the form data.
     * There is no widget for activityId and endPoints may need to be set by default in the formData, so it has to be initialized.
     */
    setFormData(property, newValue) {
        if (property.indexOf("protocols") === 0) {
            let [p, item] = property.split(":");
            this.formData[p][item] = newValue;
        } else {
            this.formData[property] = newValue;
        }
    }

    /**
     * Allows inputs on the form to communicate their values as they change.
     */
    updateFormData(property, newValue) {
        this.setFormData(property, newValue);
    }

    setAdditionalAnalytics(analyticsData) {
        this.additionalAnalytics = analyticsData;
    }

    /**
     * Action to add a subscription to an activity, or update an existing subscription
     */
    subscription = flow(function* (subscribed) {
        let formData = this.formData;
        let protocols = this._formatProtocolData();
        let operationName = "submitSectionNotification";
        let type, analyticsType;
        let surrogateId = NotifyUtils.getSurrogateIdForActivityId(formData.activityId);
        let url = 'api/notifications/' + surrogateId;

        this.actionPendingItem = formData.activityId;

        if (subscribed) {
            // update a subscription
            type = 'PUT';
            analyticsType = "Update";
        } else {
            // Create a new subscription
            type = 'POST';
            analyticsType = "Add";
        }

        url = url + '?protocols=' + protocols.join(',');

        let additionalData = this._formatAnalyticsData(surrogateId, protocols);
        let termId = TermUtils.makeTermIdFromTermName(additionalData.quarter + " " + additionalData.year);

        const labels = this.analyticsStore.label(MetricLabels.quarter, termId).get();
        this.analyticsStore.addEvent(analyticsType + "-Notify-Subscription",labels, additionalData);

        try {
            if (type === 'POST') {
                yield appStore.getHttpClient().doPost( url, null, operationName);
            } else if (type === 'PUT') {
                yield appStore.getHttpClient().doPut( url, null, operationName);
            }

            // Clear any previous error messages.
            feedbackMessagesStore.clearMessage(operationName);

            this.state = States.done;

            // Announce that what happened  ...
            events.emit(NotifyEvents.added, formData.activityId);
        } catch (error) {
            this.state = States.error;

        }
    }.bind(this));

    /**
     * Action to delete a subscription from an activity
     */
    deleteSubscription = flow(function* (subscribed) {
        let formData = this.formData;

        this.state = States.pending;

        this.actionPendingItem = formData.activityId;

        let operationName = "removeSectionNotification";

        let surrogateId = NotifyUtils.getSurrogateIdForActivityId(formData.activityId);

        let url = 'api/notifications/' + surrogateId;

        let additionalData = this._formatAnalyticsData(surrogateId);
        let termId = TermUtils.makeTermIdFromTermName(additionalData.quarter + " " + additionalData.year);

        const labels = this.analyticsStore.label(MetricLabels.quarter, termId).get();
        this.analyticsStore.addEvent("Remove-Notify-Subscription",labels, additionalData);

        try {
            yield appStore.getHttpClient().doDelete(url, operationName);
            // Clear any previous error messages.
            feedbackMessagesStore.clearMessage(operationName);
            // Check for a success short-circuit and reload data.
            this.state = States.done;
            events.emit(NotifyEvents.deleted, formData.activityId);
        } catch (error) {
            this.state = States.error;
        }
    }.bind(this));

    /**
     * Creates a list of protocols.
     */
    _formatProtocolData() {
        let formData = this.formData;

        let protocols = [];
        for (let key in formData.protocols) {
            if (formData.protocols[key]) {
                protocols.push(key);
            }
        }

        return protocols;
    }

    _formatAnalyticsData(surrogateId, protocols) {
        // surrogateId string (e.g. "2020,summer,biol,180,aa")
        // protocols list (e.g. ["email", "sms"])
        let arrSurrogateId = surrogateId.split(",");

        let additionalData = {
            cCode: arrSurrogateId[2].toUpperCase() + " " + arrSurrogateId[3],
            sCode: arrSurrogateId[4].toUpperCase(),
            quarter: capitalize(arrSurrogateId[1]),
            year: arrSurrogateId[0]
        };

        if (protocols) {
            additionalData.protocols = protocols.join(",").toUpperCase();
        }

        if (this.additionalAnalytics) {
            additionalData = {
                ...additionalData,
                ...this.additionalAnalytics
            }
        }

        return additionalData;
    }

    /**
     * Clears the formData in the state
     */
    clearFormData() {
        this.formData = this.getDefaultFormData();
    }
}

export default new NotifySubscriptionFormStore();